import { supabase } from '@/integrations/supabase/client';
import type { RealtimeChannel } from '@supabase/supabase-js';

export interface SignalData {
  type: 'offer' | 'answer' | 'ice-candidate';
  data: RTCSessionDescriptionInit | RTCIceCandidateInit;
}

export interface ParticipantInfo {
  peerId: string;
  userId: string;
  displayName: string;
  isOnline: boolean;
}

export class SignalingService {
  private channel: RealtimeChannel | null = null;
  private sessionId: string;
  private peerId: string;
  private onSignalCallback?: (fromPeerId: string, signal: SignalData) => void;
  private onParticipantJoinCallback?: (participant: ParticipantInfo) => void;
  private onParticipantLeaveCallback?: (peerId: string) => void;

  constructor(sessionId: string, peerId: string) {
    this.sessionId = sessionId;
    this.peerId = peerId;
  }

  async join(userId: string, displayName: string) {
    console.log('[Signaling] Joining session:', this.sessionId, 'as', this.peerId);

    // Insert participant record
    const { error: insertError } = await supabase
      .from('session_participants')
      .insert({
        session_id: this.sessionId,
        user_id: userId,
        peer_id: this.peerId,
        display_name: displayName,
        is_online: true
      });

    if (insertError) {
      console.error('[Signaling] Error inserting participant:', insertError);
      throw insertError;
    }

    // Set up realtime subscriptions
    this.channel = supabase.channel(`session:${this.sessionId}`, {
      config: {
        broadcast: { self: true }
      }
    });

    // Listen for signals
    this.channel
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'session_signals',
          filter: `to_peer_id=eq.${this.peerId}`
        },
        (payload) => {
          console.log('[Signaling] Received signal:', payload);
          const signal = payload.new as any;
          if (this.onSignalCallback) {
            this.onSignalCallback(signal.from_peer_id, {
              type: signal.signal_type,
              data: signal.signal_data
            });
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'session_participants',
          filter: `session_id=eq.${this.sessionId}`
        },
        (payload) => {
          console.log('[Signaling] Participant joined:', payload);
          const participant = payload.new as any;
          if (participant.peer_id !== this.peerId && this.onParticipantJoinCallback) {
            this.onParticipantJoinCallback({
              peerId: participant.peer_id,
              userId: participant.user_id,
              displayName: participant.display_name,
              isOnline: participant.is_online
            });
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'session_participants',
          filter: `session_id=eq.${this.sessionId}`
        },
        (payload) => {
          console.log('[Signaling] Participant updated:', payload);
          const participant = payload.new as any;
          if (participant.peer_id !== this.peerId) {
            if (!participant.is_online && this.onParticipantLeaveCallback) {
              this.onParticipantLeaveCallback(participant.peer_id);
            }
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'session_participants',
          filter: `session_id=eq.${this.sessionId}`
        },
        (payload) => {
          console.log('[Signaling] Participant left:', payload);
          const participant = payload.old as any;
          if (participant.peer_id !== this.peerId && this.onParticipantLeaveCallback) {
            this.onParticipantLeaveCallback(participant.peer_id);
          }
        }
      )
      .subscribe();

    console.log('[Signaling] Subscribed to session channel');
  }

  async sendSignal(toPeerId: string, signal: SignalData) {
    console.log('[Signaling] Sending signal to', toPeerId, signal.type);

    const { error } = await supabase.from('session_signals').insert({
      session_id: this.sessionId,
      from_peer_id: this.peerId,
      to_peer_id: toPeerId,
      signal_type: signal.type,
      signal_data: signal.data as any
    });

    if (error) {
      console.error('[Signaling] Error sending signal:', error);
      throw error;
    }
  }

  async getParticipants(): Promise<ParticipantInfo[]> {
    const { data, error } = await supabase
      .from('session_participants')
      .select('*')
      .eq('session_id', this.sessionId)
      .eq('is_online', true)
      .neq('peer_id', this.peerId);

    if (error) {
      console.error('[Signaling] Error getting participants:', error);
      throw error;
    }

    return (data || []).map(p => ({
      peerId: p.peer_id,
      userId: p.user_id,
      displayName: p.display_name || 'Unknown',
      isOnline: p.is_online
    }));
  }

  onSignal(callback: (fromPeerId: string, signal: SignalData) => void) {
    this.onSignalCallback = callback;
  }

  onParticipantJoin(callback: (participant: ParticipantInfo) => void) {
    this.onParticipantJoinCallback = callback;
  }

  onParticipantLeave(callback: (peerId: string) => void) {
    this.onParticipantLeaveCallback = callback;
  }

  async leave() {
    console.log('[Signaling] Leaving session');

    // Update participant status to offline
    await supabase
      .from('session_participants')
      .update({ is_online: false })
      .eq('peer_id', this.peerId);

    // Unsubscribe from channel
    if (this.channel) {
      await supabase.removeChannel(this.channel);
      this.channel = null;
    }
  }

  async cleanup() {
    // Delete participant record
    await supabase
      .from('session_participants')
      .delete()
      .eq('peer_id', this.peerId);
  }
}
